import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

import styled from '@mint-lib/styled';

import { ROW_HEIGHT } from '../../../../constants/campaignBuilder.js';
import { useGetCampaignData } from '../../../../hooks/useGetCampaignData.js';
import {
  useTablePerformanceTracking,
  useTrackingInitiator,
} from '../../../../hooks/usePerformanceTracking.js';
import { useTableContentContext } from '../../../../hooks/useTableContentContext.js';
import { useTableScale } from '../../../../hooks/useTableScale.js';
import { TablePropsType } from '../../../../types/campaignBuilder.js';
import { getScrollbarWidth } from '../../../../utils/helpers.js';
import DragLayer from '../../shared/DragLayer.jsx';
import InnerElement from '../InnerElement.jsx';
import RenderRow from '../RenderRow.jsx';

const BuilderTable = memo(function BuilderTable({
  rows,
  toggleRowExpanded,
  columnResizing,
  columns,
  ...tableProps
}: TablePropsType) {
  const scale = useTableScale();
  const { data: campaign } = useGetCampaignData();
  const startMeasurePerformance = useTrackingInitiator();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const columnHeadRef = useRef<HTMLTableRowElement>(null);
  const listRef = useRef<VariableSizeList | null>(null);

  useTableContentContext({
    ...tableProps,
    startMeasurePerformance,
    rows,
    columns,
    columnResizing,
    toggleRowExpanded,
    wrapperRef,
  });

  useEffect(() => {
    listRef?.current?.resetAfterIndex(0, true);
  }, [rows]);

  useEffect(() => {
    listRef?.current?.resetAfterIndex(0, true);
    wrapperRef.current?.scrollTo({ top: 0, left: 0 });
  }, [scale]);

  useEffect(() => {
    if (campaign?.isCarbonNeutralAllowed) {
      wrapperRef.current?.scrollTo({
        top: wrapperRef.current?.scrollTop,
        left: wrapperRef.current?.scrollWidth,
        behavior: 'smooth',
      });
    }
  }, [campaign?.isCarbonNeutralAllowed]);

  useTablePerformanceTracking('BuilderTable');

  const columnsWidth = useMemo(
    () =>
      columns.reduce((acc, item) => {
        const widthItem =
          typeof item.width === 'number' ? item.width * scale : 0;

        return acc + widthItem;
      }, 0),
    [columns, scale],
  );

  const getItemSize = useCallback(
    (index: number) => {
      return ROW_HEIGHT * scale;
    },
    [rows, scale],
  );

  const scrollbarWidth = useMemo(getScrollbarWidth, []);

  const getTableHeight = (maxHeight: number, maxVisibleWidth: number) => {
    const isHorizontalScrollVisible = columnsWidth > maxVisibleWidth;

    const estimatedHeight =
      (rows.length + 1) * ROW_HEIGHT * scale +
      (isHorizontalScrollVisible ? scrollbarWidth : 0);

    return Math.min(estimatedHeight, maxHeight);
  };

  return (
    <>
      <AutoSizer>
        {({ height, width }) => (
          <StyledVariableSizeList
            height={getTableHeight(height, width)}
            itemCount={rows.length}
            itemSize={getItemSize}
            width={width}
            innerElementType={InnerElement}
            outerRef={wrapperRef}
            innerRef={columnHeadRef}
            ref={listRef}
            className="variableSizeList"
          >
            {RenderRow}
          </StyledVariableSizeList>
        )}
      </AutoSizer>
      <DragLayer scale={scale} resize={columnResizing?.columnWidths} />
    </>
  );
});

const StyledVariableSizeList = styled(VariableSizeList)`
  border-radius: 8px;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.15),
    0px 0px 5px 0px rgba(23, 24, 24, 0.05);
`;

export default BuilderTable;
